import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus, Search, FileText, DollarSign, Edit, Trash2, X, CheckCircle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const HOST = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const API_PAGOS = `${HOST}/api/v1/public/pagos/`;
const API_TENANTS = `${HOST}/api/v1/public/tenants/`;

const MESES = [
    { id: 1, name: 'Enero' }, { id: 2, name: 'Febrero' }, { id: 3, name: 'Marzo' },
    { id: 4, name: 'Abril' }, { id: 5, name: 'Mayo' }, { id: 6, name: 'Junio' },
    { id: 7, name: 'Julio' }, { id: 8, name: 'Agosto' }, { id: 9, name: 'Septiembre' },
    { id: 10, name: 'Octubre' }, { id: 11, name: 'Noviembre' }, { id: 12, name: 'Diciembre' }
];

const INITIAL_FORM = {
    id: null,
    tenant: '',
    plan: '',
    monto: '',
    nro_comprobante: '',
    forma_pago: 'efectivo',
    nro_transaccion: '',
    estado: 'pagado',
    observacion: '',
    mes_referencia: new Date().getMonth() + 1,
    anio_referencia: new Date().getFullYear(),
    cotizacion_usada: 0
};

export default function FacturacionPlataforma() {
    const [pagos, setPagos] = useState([]);
    const [tenants, setTenants] = useState([]);
    const [, setLoading] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [search, setSearch] = useState('');
    const [formData, setFormData] = useState(INITIAL_FORM);


    const fetchData = async () => {
        setLoading(true);
        try {
            const [pRes, tRes] = await Promise.all([
                axios.get(API_PAGOS),
                axios.get(API_TENANTS)
            ]);
            setPagos(pRes.data);
            setTenants(tRes.data);
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchData(); }, []);

    const handleOpenModal = (pago = null) => {
        if (pago) {
            setFormData({
                id: pago.id,
                tenant: pago.tenant,
                plan: pago.plan || '',
                monto: pago.monto,
                nro_comprobante: pago.nro_comprobante || '',
                forma_pago: pago.forma_pago || 'efectivo',
                nro_transaccion: pago.nro_transaccion || '',
                estado: pago.estado,
                observacion: pago.observacion || '',
                mes_referencia: pago.mes_referencia || new Date().getMonth() + 1,
                anio_referencia: pago.anio_referencia || new Date().getFullYear(),
                cotizacion_usada: pago.cotizacion_usada || 0
            });
        } else {
            setFormData(INITIAL_FORM);
        }
        setIsModalOpen(true);
    };

    const handleSave = async (e) => {
        e.preventDefault();
        try {
            if (formData.id) {
                await axios.put(`${API_PAGOS}${formData.id}/`, formData);
            } else {
                await axios.post(API_PAGOS, formData);
            }
            setIsModalOpen(false);
            fetchData();
        } catch (e) {
            alert("Error al procesar el pago");
        }
    };

    const deletePago = async (id) => {
        if (window.confirm("¿Eliminar registro?")) {
            await axios.delete(`${API_PAGOS}${id}/`);
            fetchData();
        }
    };

    const filteredPagos = pagos.filter(p =>
        p.tenant_name.toLowerCase().includes(search.toLowerCase()) ||
        p.nro_comprobante?.toLowerCase().includes(search.toLowerCase())
    );

    const totalIngresos = pagos.reduce((acc, p) => p.estado === 'pagado' ? acc + parseFloat(p.monto) : acc, 0);

    return (
        <div className="space-y-6 pb-20">
            {/* Header */}
            <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center bg-white p-6 rounded-[2.5rem] shadow-sm border border-slate-100 gap-4">
                <div className="flex items-center gap-4">
                    <div className="bg-brand-100 p-4 rounded-2xl text-brand-800">
                        <FileText size={24} />
                    </div>
                    <div>
                        <h2 className="titulo-modal">Caja SaaS</h2>
                        <p className="text-[10px] text-slate-500 font-bold uppercase tracking-widest">Registro de Cobranzas</p>
                    </div>
                </div>

                <div className="flex gap-3 w-full lg:w-auto">
                    <div className="relative flex-1 lg:w-64">
                        <Search className="icono-campo" size={18} />
                        <input
                            type="text"
                            placeholder="Buscar por cliente o factura..."
                            className="w-full bg-slate-50 border border-slate-200 rounded-xl py-2.5 pl-10 pr-4 text-sm font-medium outline-none"
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                        />
                    </div>
                    <button
                        onClick={() => handleOpenModal()}
                        className="bg-slate-900 text-white px-5 py-2.5 rounded-xl font-black text-xs uppercase tracking-widest hover:bg-slate-800 shadow-lg flex items-center gap-2"
                    >
                        <Plus size={18} /> Nuevo Cobro
                    </button>
                </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-negro-900 p-6 rounded-[2rem] text-white shadow-xl shadow-negro-900/20 relative overflow-hidden">
                    <p className="text-xs font-bold uppercase tracking-widest opacity-80">Recaudación Total</p>
                    <h4 className="text-3xl font-black mt-1">Gs. {totalIngresos.toLocaleString()}</h4>
                    <DollarSign className="absolute -bottom-4 -right-4 w-24 h-24 opacity-10" />
                </div>
                <div className="bg-white p-6 rounded-[2rem] border border-slate-100 shadow-sm flex items-center gap-4">
                    <div className="bg-brand-50 p-3 rounded-2xl text-brand-800">
                        <CheckCircle size={24} />
                    </div>
                    <div>
                        <p className="text-xs font-bold text-slate-400 uppercase">Cobros Registrados</p>
                        <p className="text-2xl font-black text-slate-800">{pagos.length}</p>
                    </div>
                </div>
            </div>

            {/* Table */}
            <div className="bg-white rounded-[2.5rem] shadow-sm border border-slate-100 overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-left text-sm">
                        <thead>
                            <tr className="bg-slate-50 text-slate-500 border-b border-slate-100">
                                <th className="tabla-encabezado">Inquilino</th>
                                <th className="tabla-encabezado">Periodo / Comprobante</th>
                                <th className="tabla-encabezado">Cobro</th>
                                <th className="px-6 py-4 font-bold uppercase tracking-wider text-[11px] text-right">Acciones</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 font-medium whitespace-nowrap">
                            {filteredPagos.length === 0 && (
                                <tr><td colSpan={4} className="text-center py-12 text-slate-400 text-sm">Sin cobros registrados.</td></tr>
                            )}
                            {filteredPagos.map(p => (
                                <tr key={p.id} className="hover:bg-slate-50/50 transition-colors">
                                    <td className="px-6 py-4">
                                        <div className="font-bold text-slate-800">{p.tenant_name}</div>
                                        <div className="text-[10px] text-slate-400 font-mono">#{p.id}</div>
                                    </td>
                                    <td className="px-6 py-4">
                                        <div className="font-bold text-slate-700">
                                            {p.mes_referencia ? `${MESES.find(m => m.id === p.mes_referencia)?.name} ${p.anio_referencia}` : '—'}
                                        </div>
                                        <div className="text-[10px] text-brand-800 uppercase font-black">Fact: {p.nro_comprobante || 'S/N'}</div>
                                    </td>
                                    <td className="px-6 py-4">
                                        <div className="font-black text-slate-800">Gs. {parseFloat(p.monto).toLocaleString()}</div>
                                        <div className="text-[10px] text-slate-400 font-bold uppercase tracking-widest">
                                            {p.forma_pago}{p.nro_transaccion ? ` · ${p.nro_transaccion}` : ''}
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 text-right">
                                        <div className="flex justify-end gap-2">
                                            <button onClick={() => handleOpenModal(p)} className="accion-fila">
                                                <Edit size={16} />
                                            </button>
                                            <button onClick={() => deletePago(p.id)} className="p-2 text-slate-300 hover:text-error-500 transition-colors">
                                                <Trash2 size={16} />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Modal */}
            <AnimatePresence>
                {isModalOpen && (
                    <div className="fondo-modal">
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.95 }}
                            className="bg-white rounded-[2.5rem] w-full max-w-md shadow-2xl p-8 max-h-[90vh] overflow-y-auto"
                        >
                            <div className="flex justify-between items-center mb-6">
                                <h3 className="text-2xl font-black text-slate-800 tracking-tight">
                                    {formData.id ? 'Editar Cobro' : 'Registro de Cobro'}
                                </h3>
                                <button onClick={() => setIsModalOpen(false)} className="bg-slate-100 p-2 rounded-full hover:bg-slate-200 transition-colors">
                                    <X size={24} />
                                </button>
                            </div>

                            <form onSubmit={handleSave} className="space-y-4">
                                {/* Inquilino */}
                                <div>
                                    <label htmlFor="pago-inquilino" className="etiqueta-campo">Inquilino *</label>
                                    <select id="pago-inquilino"
                                        required
                                        className="campo"
                                        value={formData.tenant}
                                        onChange={e => {
                                            const t = tenants.find(it => String(it.id) === e.target.value);
                                            setFormData({ ...formData, tenant: e.target.value, plan: t?.plan || '', monto: t?.plan_price || '' });
                                        }}
                                    >
                                        <option value="">Seleccione un inquilino</option>
                                        {tenants.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                                    </select>
                                </div>

                                {/* Factura + Monto */}
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label htmlFor="pago-nro-factura" className="etiqueta-campo">Nro. Factura</label>
                                        <input id="pago-nro-factura"
                                            type="text"
                                            className="campo"
                                            placeholder="001-001-0000001"
                                            value={formData.nro_comprobante}
                                            onChange={e => setFormData({ ...formData, nro_comprobante: e.target.value })}
                                        />
                                    </div>
                                    <div>
                                        <label htmlFor="pago-monto-gs" className="etiqueta-campo">Monto (Gs.) *</label>
                                        <input id="pago-monto-gs"
                                            required type="number"
                                            className="w-full bg-brand-50 border border-brand-100 rounded-2xl px-4 py-3 text-lg font-black text-exito-700 outline-none"
                                            value={formData.monto}
                                            onChange={e => setFormData({ ...formData, monto: e.target.value })}
                                        />
                                    </div>
                                </div>

                                {/* Forma de pago + Referencia */}
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label htmlFor="pago-forma-de-pago" className="etiqueta-campo">Forma de Pago</label>
                                        <select id="pago-forma-de-pago"
                                            className="campo"
                                            value={formData.forma_pago}
                                            onChange={e => setFormData({ ...formData, forma_pago: e.target.value })}
                                        >
                                            <option value="efectivo">Efectivo</option>
                                            <option value="transferencia">Transferencia</option>
                                            <option value="tarjeta">Tarjeta</option>
                                            <option value="otros">Otros</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label htmlFor="pago-nro-transaccion-ref" className="etiqueta-campo">Nro. Transacción / Ref.</label>
                                        <input id="pago-nro-transaccion-ref"
                                            type="text"
                                            className="campo"
                                            placeholder="Boleta # / Hash"
                                            value={formData.nro_transaccion}
                                            onChange={e => setFormData({ ...formData, nro_transaccion: e.target.value })}
                                        />
                                    </div>
                                </div>

                                {/* Periodo */}
                                <div className="grid grid-cols-2 gap-4 pt-2 border-t border-slate-100">
                                    <div>
                                        <label htmlFor="pago-mes" className="etiqueta-campo">Mes</label>
                                        <select id="pago-mes"
                                            className="campo"
                                            value={formData.mes_referencia}
                                            onChange={e => setFormData({ ...formData, mes_referencia: parseInt(e.target.value) })}
                                        >
                                            {MESES.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
                                        </select>
                                    </div>
                                    <div>
                                        <label htmlFor="pago-ano" className="etiqueta-campo">Año</label>
                                        <input id="pago-ano"
                                            type="number"
                                            className="campo"
                                            value={formData.anio_referencia}
                                            onChange={e => setFormData({ ...formData, anio_referencia: parseInt(e.target.value) })}
                                        />
                                    </div>
                                </div>

                                {/* Observaciones */}
                                <div>
                                    <label htmlFor="pago-observaciones" className="etiqueta-campo">Observaciones</label>
                                    <textarea id="pago-observaciones"
                                        className="w-full bg-slate-50 border border-slate-200 rounded-2xl px-4 py-3 text-sm font-bold outline-none min-h-[60px] resize-none"
                                        placeholder="Notas internas del cobro..."
                                        value={formData.observacion}
                                        onChange={e => setFormData({ ...formData, observacion: e.target.value })}
                                    />
                                </div>

                                <div className="flex gap-3 pt-4">
                                    <button type="button" onClick={() => setIsModalOpen(false)} className="flex-1 bg-slate-100 text-slate-600 font-black uppercase tracking-widest py-4 rounded-2xl hover:bg-slate-200 transition-colors">
                                        Cancelar
                                    </button>
                                    <button className="flex-1 bg-slate-900 text-white font-black uppercase tracking-widest py-4 rounded-2xl shadow-xl hover:bg-slate-800 transition-colors">
                                        {formData.id ? 'Actualizar' : 'Confirmar Cobro'}
                                    </button>
                                </div>
                            </form>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>
        </div>
    );
}
